/**
 * Feature Flag Context - Remote Control System
 *
 * This context provides centralized feature flag management with remote control:
 * - Fetches flags from backend API on mount
 * - Auto-refreshes every 60 seconds
 * - Falls back to safe defaults if API fails
 * - Enables instant rollback via Vercel environment variables
 *
 * Usage:
 *   const isPaywallEnabled = useFeatureFlag('PAYWALL');
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { fetchFeatureFlags, fetchPublicFeatureFlags } from '../services/featureFlagService';
import { logger } from '../lib/logging';
import { useAuth } from './AuthContext';
import { entitlementService } from '../services/entitlement';

// ============================================================================
// TYPES
// ============================================================================

export type FeatureFlag =
  | 'ANONYMOUS'
  | 'PAYWALL'
  | 'TOKENUSAGE'
  | 'PM'
  | 'BETA';

interface FeatureFlagContextType {
  // Check if flag is enabled
  isEnabled: (flag: FeatureFlag) => boolean;

  // Refresh flags from server
  refresh: () => Promise<void>;

  // Clear localStorage override for a specific flag
  clearLocalOverride: (flag: FeatureFlag) => void;

  // Clear all localStorage overrides
  clearAllLocalOverrides: () => void;

  // Loading state
  loading: boolean;

  // Error state
  error: string | null;

  // Last fetch timestamp
  lastFetch: Date | null;
}

// ============================================================================
// DEFAULT VALUES (Safe fallbacks if API fails)
// ============================================================================

const DEFAULT_FLAG_VALUES: Record<FeatureFlag, boolean> = {
  ANONYMOUS: false, // Default: anonymous users disabled (auth required)
  PAYWALL: true, // Default: enforce token limits (fail-closed for security)
  TOKENUSAGE: false, // Default: Token usage tracking disabled (beta testing only)
  PM: false, // Default: Product Manager features disabled (internal only)
  BETA: false, // Default: Beta access features disabled (gating beta user access and restriction flows)
  // Note: ENABLE_LLM_JUDGE has been removed as LLM Judge scoring is now always enabled (hardcoded)
  // Note: RESULTS_NEW_DESIGN has been removed as the new results design is now always enabled (hardcoded)
  // Note: ENABLE_READABLE_URLS has been removed as readable URLs are now always enabled (hardcoded)
  // Note: NEW_DESIGN_BETA has been removed as Phase 2 redesign features are now always enabled (hardcoded)
};

// ============================================================================
// CONTEXT
// ============================================================================

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
  const [flagValues, setFlagValues] = useState<Record<FeatureFlag, boolean>>(DEFAULT_FLAG_VALUES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);
  const { user, loading: authLoading } = useAuth();

  // Generate correlation ID for logging
  const correlationId = React.useMemo(() => crypto.randomUUID(), []);

  // Fetch flags from backend
  const loadFlags = useCallback(async () => {
    // Don't start new fetch if auth is still loading
    if (authLoading) {
      return;
    }

    // If no user, use defaults and don't attempt to fetch
    if (!user) {
      setFlagValues(DEFAULT_FLAG_VALUES);
      setLastFetch(null);
      setError(null);  // Clear error - this is expected state
      setLoading(false);
      return;
    }

    // Set loading true only if we don't have data yet
    setLoading(true);

    try {
      // Check if user is admin
      const isAdmin = user.email === 'pavlo.kosmos@gmail.com' ||
                      user.email?.endsWith('@smartprompt.ai');

      logger.info('feature_flags.fetch_start', undefined, {
        op: 'fetch_flags',
        component: 'FeatureFlagProvider',
        correlationId,
        isAdmin,
        userEmail: user.email
      });

      // Use different endpoints based on user type
      let remoteFlags: Map<string, boolean>;

      if (isAdmin) {
        // Admin users: use admin endpoint with auth token
        const authToken = await user.getIdToken();
        remoteFlags = await fetchFeatureFlags(authToken);
        console.log('[FeatureFlags] Admin user - fetched from /api/admin/flags');
      } else {
        // Non-admin users: use public endpoint with auth token for per-user overrides
        const authToken = await user.getIdToken();
        console.log('[FeatureFlags] Non-admin user - sending request with auth token:', {
          userId: user.uid,
          hasToken: !!authToken,
          tokenPreview: authToken ? `${authToken.substring(0, 20)}...` : 'none'
        });
        remoteFlags = await fetchPublicFeatureFlags(authToken);
        console.log('[FeatureFlags] Non-admin user - fetched from /api/flags');
      }

      // Merge remote flags with defaults (remote takes precedence)
      const merged: Record<string, boolean> = { ...DEFAULT_FLAG_VALUES };

      remoteFlags.forEach((value, key) => {
        if (key in DEFAULT_FLAG_VALUES) {
          merged[key] = value;
        }
      });

      // Override PAYWALL flag with value from entitlement service (most reliable)
      // The entitlement service has working per-user PAYWALL logic
      try {
        const entitlementStatus = await entitlementService.getStatus();
        merged.PAYWALL = entitlementStatus.paywallEnabled;
        console.log('[FeatureFlags] Overrode PAYWALL flag with entitlement service value:', {
          paywallEnabled: entitlementStatus.paywallEnabled,
          source: 'entitlement-service'
        });
      } catch (error) {
        console.warn('[FeatureFlags] Failed to fetch paywall status from entitlement service:', error);
        // Keep the value from /api/flags if entitlement service fails
      }

      setFlagValues(merged as Record<FeatureFlag, boolean>);
      setLastFetch(new Date());
      setError(null);

      // Auto-clear stale localStorage overrides that conflict with backend
      Object.keys(merged).forEach((key) => {
        const localOverride = localStorage.getItem(`feature_flag_${key}`);
        if (localOverride !== null) {
          const localValue = localOverride === 'true';
          const remoteValue = merged[key];

          if (localValue !== remoteValue) {
            console.log(
              `[FeatureFlags] Clearing stale localStorage override for ${key}:`,
              `localStorage=${localValue}, remote=${remoteValue}`
            );
            localStorage.removeItem(`feature_flag_${key}`);

            logger.info('feature_flags.clear_stale_override', undefined, {
              op: 'clear_stale_override',
              component: 'FeatureFlagProvider',
              correlationId,
              flagKey: key,
              localValue,
              remoteValue
            });
          }
        }
      });

      logger.info('feature_flags.fetch_success', undefined, {
        op: 'fetch_flags',
        component: 'FeatureFlagProvider',
        correlationId,
        success: true,
        flagCount: remoteFlags.size,
        flags: merged
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      logger.error('feature_flags.fetch_error', undefined, {
        op: 'fetch_flags',
        component: 'FeatureFlagProvider',
        correlationId,
        success: false,
        err_message: errorMessage
      });

      // Silently fallback to defaults - don't block app for flag fetch failures
      setFlagValues(DEFAULT_FLAG_VALUES);
      setError(null);  // Clear error to avoid blocking UI
      console.warn('[FeatureFlags] Using default values due to fetch error:', errorMessage);
    } finally {
      // CRITICAL: Always exit loading state
      setLoading(false);
    }
  }, [authLoading, correlationId, user]);

  // Load flags on mount and when auth state changes
  useEffect(() => {
    logger.info('feature_flags.provider_mount', undefined, {
      op: 'provider_mount',
      component: 'FeatureFlagProvider',
      correlationId,
      authLoading,
      hasUser: !!user
    });

    // Wait for auth to complete before loading flags
    if (!authLoading) {
      loadFlags();
    }

    return () => {
      logger.info('feature_flags.provider_unmount', undefined, {
        op: 'provider_unmount',
        component: 'FeatureFlagProvider',
        correlationId
      });
    };
  }, [loadFlags, correlationId, authLoading, user]);

  // Periodically refresh flags (every 60 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      logger.info('feature_flags.auto_refresh', undefined, {
        op: 'auto_refresh',
        component: 'FeatureFlagProvider',
        correlationId,
        lastFetch
      });
      loadFlags();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [loadFlags, correlationId, lastFetch]);

  // API: Check if flag is enabled
  const isEnabled = useCallback((flag: FeatureFlag): boolean => {
    // Priority 1: Check localStorage override (for local testing via admin page)
    const localOverride = localStorage.getItem(`feature_flag_${flag}`);
    if (localOverride !== null) {
      const value = localOverride === 'true';
      if (import.meta.env.DEV) {
        console.log(`[FeatureFlags] Check ${flag}:`, value, '(source: localStorage)');
      }
      return value;
    }

    // Priority 2: Check remote/cached value
    const value = flagValues[flag] ?? DEFAULT_FLAG_VALUES[flag];

    // Log flag check for debugging (only in development)
    if (import.meta.env.DEV) {
      console.log(`[FeatureFlags] Check ${flag}:`, value);
    }

    return value;
  }, [flagValues]);

  // API: Clear localStorage overrides for a specific flag
  const clearLocalOverride = useCallback((flag: FeatureFlag) => {
    localStorage.removeItem(`feature_flag_${flag}`);
  }, []);

  // API: Clear all localStorage overrides
  const clearAllLocalOverrides = useCallback(() => {
    Object.keys(localStorage)
      .filter(key => key.startsWith('feature_flag_'))
      .forEach(key => localStorage.removeItem(key));
  }, []);

  // API: Manual refresh
  const refresh = useCallback(async () => {
    setLoading(true);
    await loadFlags();
  }, [loadFlags]);

  const value: FeatureFlagContextType = {
    isEnabled,
    refresh,
    loading,
    error,
    lastFetch,
    clearLocalOverride,
    clearAllLocalOverrides
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Main hook to access feature flags context
 *
 * @example
 * const { isEnabled, refresh, loading } = useFeatureFlags();
 * if (loading) return <Spinner />;
 * const showPaywall = isEnabled('PAYWALL');
 */
export function useFeatureFlags(): FeatureFlagContextType {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
  }
  return context;
}

/**
 * Hook to check a specific flag (auto-updates when remote value changes)
 *
 * @param flag - The feature flag to check
 * @returns boolean - True if flag is enabled
 *
 * @example
 * const isPaywallEnabled = useFeatureFlag('PAYWALL');
 * return isPaywallEnabled ? <PaywallModal /> : <FreeContent />;
 */
export function useFeatureFlag(flag: FeatureFlag): boolean {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flag);
}

// ============================================================================
// STANDALONE FUNCTION (for non-React contexts)
// ============================================================================

/**
 * Check flag value without React context (for utility functions)
 * Note: This won't auto-update - use hook in components for reactive updates
 *
 * @param flag - The feature flag to check
 * @returns boolean - True if flag is enabled (uses default if context unavailable)
 */
export function checkFeatureFlag(flag: FeatureFlag): boolean {
  // This is a fallback for non-React contexts
  // In React components, always use useFeatureFlag() hook
  console.warn('[FeatureFlags] Using checkFeatureFlag outside React context - value may be stale');
  return DEFAULT_FLAG_VALUES[flag];
}
